import React from 'react';
import { Modal, Loader, TextInput, ActionIcon, Checkbox, SegmentedControl, NativeSelect, FileButton } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEye,
  faEyeSlash,
  faBold,
  faMinus,
  faCopy,
  faGripVertical,
  faArrowUp,
  faArrowDown,
  faChevronDown,
  faChevronUp,
} from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { ReportView, ZReportLayoutConfig } from 'shared-models/browser';
import { Button } from '../../../../components/Button';
import { PosReportView } from './PosReportView';
import {
  mergeZReportLayoutConfig,
  buildDefaultZReportLayoutConfig,
} from './z-report-layout.catalog';
import {
  buildLayoutExportFile,
  buildLayoutExportFilename,
  parseLayoutImportFile,
} from './z-report-layout.transfer';
import classes from './z-report-layout-modal.module.scss';

type ZReportLayoutModalProps = {
  opened: boolean;
  loading: boolean;
  saving: boolean;
  initialConfig: ZReportLayoutConfig | null;
  previewReport?: ReportView | null;
  tenantId?: string;
  onClose: () => void;
  onSave: (config: ZReportLayoutConfig | null) => Promise<boolean>;
};

type LayoutEntry = ZReportLayoutConfig['entries'][number];

type SectionGroup = {
  section: LayoutEntry;
  sectionIndex: number;
  rows: Array<{ entry: LayoutEntry; index: number }>;
};

type DropTarget =
  | { kind: 'section'; sectionKey: string }
  | { kind: 'section-start'; sectionKey: string }
  | { kind: 'section-end'; sectionKey: string }
  | { kind: 'section-body'; sectionKey: string }
  | { kind: 'row'; rowKey: string; after: boolean };

const FALLBACK_Z_PREVIEW_REPORT: ReportView = {
  kind: 'z',
  vatRatePercent: 18,
  header: 'תקשורת קפה',
  zReportLayoutConfig: null,
  sessionId: 120,
  sessionName: 'POS/00120',
  openTime: '2026-08-20T07:00:00.000Z',
  closeTime: '2026-08-20T16:00:00.000Z',
  printTime: '2026-08-20T16:01:00.000Z',
  openerName: 'Manager',
  workerName: null,
  rangeFrom: null,
  rangeTo: null,
  salesWithOpenOrders: 0,
  closedRevenue: 1824,
  openOrdersTotal: 0,
  openOrdersCount: 0,
  vatAmount: 278.2,
  dinersCount: 24,
  canceledItemsCount: 2,
  canceledOrderlinesCount: 2,
  canceledItemsAmount: 32,
  discountsAmount: 45,
  canceledOrdersCount: 1,
  avgPerDiner: 76,
  avgPerDinerTakeAway: 38,
  tipsTotal: 120,
  salesByReportGroup: { food: 1140, beverages: 520, other: 164 },
  openOrdersSalesByReportGroup: { food: 0, beverages: 0, other: 0 },
  takeAwayRevenue: 380,
  takeAwayVatAmount: 57.9,
  takeAwayTips: 18,
  takeAwayDinersCount: 10,
  dineInRevenue: 1444,
  dineInVatAmount: 220.3,
  dineInTips: 102,
  dineInDinersCount: 14,
  dineInAvgPerDiner: 103.1,
  tipsCredit: 82,
  tipsCash: 38,
  receiptsCash: 852,
  receiptsCredit: 1092,
  receiptsTotal: 1944,
  paymentsCredit: 1010,
  paymentsCash: 814,
  paymentsTotal: 1824,
  closedPaymentsCredit: 1010,
  closedPaymentsCash: 814,
  openPaymentsCredit: 0,
  openPaymentsCash: 0,
  creditCharges: 1030,
  creditRefunds: 20,
  creditTotal: 1010,
  cashCharges: 840,
  cashRefunds: 26,
  cashTotal: 814,
  openingCash: 250,
  closingCash: 2100,
  shiftCloseCashBalancesEnabled: true,
  chargeTransactionsCount: 41,
  refundTransactionsCount: 2,
  othItemsCount: 0,
  othOrdersCount: 0,
  drawers: [{
    printerId: 4,
    printerName: 'קופה ראשית',
    cashTotalWithTips: 852,
    tipsTotal: 38,
    cashNetOfTips: 814,
    cashInTotal: 100,
    cashOutTotal: 40,
    cashMoves: [
      { direction: 'in', amount: 100, reason: 'הפקדת עודף', at: '2026-01-01T10:00:00.000Z', byName: 'מנהל' },
      { direction: 'out', amount: 40, reason: 'קניית ציוד', at: '2026-01-01T12:00:00.000Z', byName: 'מנהל' },
    ],
  }],
  creditCardLines: [],
};

const FALLBACK_X_PREVIEW_REPORT: ReportView = {
  ...FALLBACK_Z_PREVIEW_REPORT,
  kind: 'x',
  closeTime: null,
  salesWithOpenOrders: 2241.5,
  openOrdersTotal: 417.5,
  openOrdersCount: 6,
};

const NO_SELECTION_INPUT_PROPS = {
  onContextMenu: (event: React.MouseEvent<HTMLInputElement>) => event.preventDefault(),
  onSelect: (event: React.SyntheticEvent<HTMLInputElement>) => event.preventDefault(),
};

const X_ONLY_ROW_KEYS = new Set([
  'row:salesWithOpenOrders',
  'row:openOrdersTotal',
  'row:openOrdersCount',
]);

const MONEY_ROW_KEYS = new Set([
  'row:salesWithOpenOrders',
  'row:openOrdersTotal',
  'row:closedRevenue',
  'row:vatAmount',
  'row:canceledItemsAmount',
  'row:discountsAmount',
  'row:avgPerDiner',
  'row:avgPerDinerTakeAway',
  'row:tipsTotalMain',
  'row:salesByReportGroupFood',
  'row:salesByReportGroupBeverages',
  'row:salesByReportGroupOther',
  'row:tipsCredit',
  'row:tipsCash',
  'row:tipsTotalSection',
  'row:tipsStationary',
  'row:receiptsCash',
  'row:receiptsCredit',
  'row:receiptsTotal',
  'row:paymentsCredit',
  'row:paymentsCash',
  'row:paymentsTotal',
  'row:creditCharges',
  'row:creditRefunds',
  'row:creditTotal',
  'row:cashCharges',
  'row:cashRefunds',
  'row:cashTotal',
  'row:drawerCashTotalWithTips',
  'row:drawerTipsTotal',
  'row:cashMovesNet',
  'row:openingCash',
  'row:countedCash',
  'row:cashMinusCreditTips',
  'row:expectedCash',
]);

const buildSectionGroups = (entries: LayoutEntry[]): SectionGroup[] => {
  const groups: SectionGroup[] = [];
  const leadingRows: Array<{ entry: LayoutEntry; index: number }> = [];
  let current: SectionGroup | null = null;

  entries.forEach((entry, index) => {
    if (entry.type === 'section') {
      current = { section: entry, sectionIndex: index, rows: [] };
      groups.push(current);
      return;
    }
    const row = { entry, index };
    if (!current) {
      leadingRows.push(row);
      return;
    }
    current.rows.push(row);
  });

  if (leadingRows.length > 0 && groups.length > 0) {
    groups[0].rows = [...leadingRows, ...groups[0].rows];
  }

  return groups;
};

const findSectionRange = (entries: LayoutEntry[], sectionKey: string): { start: number; endExclusive: number } | null => {
  const start = entries.findIndex((entry) => entry.key === sectionKey && entry.type === 'section');
  if (start < 0) {
    return null;
  }
  let endExclusive = entries.length;
  for (let i = start + 1; i < entries.length; i += 1) {
    if (entries[i].type === 'section') {
      endExclusive = i;
      break;
    }
  }
  return { start, endExclusive };
};

const ZReportLayoutModalComponent: React.FC<ZReportLayoutModalProps> = ({
  opened,
  loading,
  saving,
  initialConfig,
  previewReport,
  tenantId,
  onClose,
  onSave,
}) => {
  const [previewKind, setPreviewKind] = React.useState<'z' | 'x'>('z');
  const effectivePreviewReport = React.useMemo(() => {
    if (previewKind === 'x') {
      if (previewReport?.kind === 'x') {
        return previewReport;
      }
      return FALLBACK_X_PREVIEW_REPORT;
    }
    if (previewReport?.kind === 'z') {
      return previewReport;
    }
    return FALLBACK_Z_PREVIEW_REPORT;
  }, [previewKind, previewReport]);
  const [config, setConfig] = React.useState<ZReportLayoutConfig>(buildDefaultZReportLayoutConfig());
  const dragPayloadRef = React.useRef<{ key: string; type: LayoutEntry['type'] } | null>(null);
  const [collapsedSections, setCollapsedSections] = React.useState<Set<string>>(new Set());
  const [dropTarget, setDropTarget] = React.useState<DropTarget | null>(null);
  const resetFileRef = React.useRef<() => void>(null);
  const [transferError, setTransferError] = React.useState<string | null>(null);
  const [importedNotice, setImportedNotice] = React.useState<string | null>(null);

  const beginDrag = React.useCallback((event: React.DragEvent<HTMLElement>, key: string, type: LayoutEntry['type']) => {
    event.stopPropagation();
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', `${type}:${key}`);
    dragPayloadRef.current = { key, type };
  }, []);

  const clearDrag = React.useCallback(() => {
    dragPayloadRef.current = null;
  }, []);

  const endDrag = React.useCallback(() => {
    clearDrag();
    setDropTarget(null);
  }, [clearDrag]);

  const currentDrag = React.useCallback(() => dragPayloadRef.current, []);

  const allowDrop = React.useCallback((event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  React.useEffect(() => {
    if (!opened) {
      return;
    }
    setConfig(mergeZReportLayoutConfig(initialConfig));
    setCollapsedSections(new Set());
    setDropTarget(null);
    setPreviewKind('z');
    setTransferError(null);
    setImportedNotice(null);
  }, [opened, initialConfig]);

  const toggleSectionCollapsed = React.useCallback((sectionKey: string) => {
    setCollapsedSections((current) => {
      const next = new Set(current);
      if (next.has(sectionKey)) {
        next.delete(sectionKey);
      } else {
        next.add(sectionKey);
      }
      return next;
    });
  }, []);

  const setEntry = React.useCallback((key: string, updater: (current: ZReportLayoutConfig['entries'][number]) => ZReportLayoutConfig['entries'][number]) => {
    setConfig((current) => ({
      ...current,
      entries: current.entries.map((entry) => (entry.key === key ? updater(entry) : entry)),
    }));
  }, []);

  const moveRowToIndex = React.useCallback((rowKey: string, rawTargetIndex: number) => {
    setConfig((current) => {
      const next = [...current.entries];
      const fromIndex = next.findIndex((entry) => entry.key === rowKey && entry.type === 'row');
      if (fromIndex < 0) {
        return current;
      }
      const [entry] = next.splice(fromIndex, 1);
      let targetIndex = Math.max(0, Math.min(rawTargetIndex, next.length));
      if (fromIndex < targetIndex) {
        targetIndex -= 1;
      }
      next.splice(targetIndex, 0, entry);
      return { ...current, entries: next };
    });
  }, []);

  const moveSectionBefore = React.useCallback((movingSectionKey: string, targetSectionKey: string) => {
    if (movingSectionKey === targetSectionKey) {
      return;
    }
    setConfig((current) => {
      const movingRange = findSectionRange(current.entries, movingSectionKey);
      const targetRange = findSectionRange(current.entries, targetSectionKey);
      if (!movingRange || !targetRange) {
        return current;
      }
      if (movingRange.start === targetRange.start) {
        return current;
      }

      const movingSlice = current.entries.slice(movingRange.start, movingRange.endExclusive);
      const next = [...current.entries];
      next.splice(movingRange.start, movingSlice.length);

      let insertIndex = targetRange.start;
      if (movingRange.start < targetRange.start) {
        insertIndex -= movingSlice.length;
      }
      next.splice(insertIndex, 0, ...movingSlice);

      return { ...current, entries: next };
    });
  }, []);

  const moveSectionByStep = React.useCallback((sectionKey: string, step: -1 | 1) => {
    const sectionKeys = config.entries.filter((entry) => entry.type === 'section').map((entry) => entry.key);
    const currentIndex = sectionKeys.indexOf(sectionKey);
    if (currentIndex < 0) {
      return;
    }
    const targetIndex = currentIndex + step;
    if (targetIndex < 0 || targetIndex >= sectionKeys.length) {
      return;
    }
    const targetSectionKey = sectionKeys[targetIndex];
    if (step < 0) {
      moveSectionBefore(sectionKey, targetSectionKey);
      return;
    }
    const sectionAfterTarget = sectionKeys[targetIndex + 1] ?? null;
    if (sectionAfterTarget) {
      moveSectionBefore(sectionKey, sectionAfterTarget);
      return;
    }
    setConfig((current) => {
      const movingRange = findSectionRange(current.entries, sectionKey);
      if (!movingRange) {
        return current;
      }
      const movingSlice = current.entries.slice(movingRange.start, movingRange.endExclusive);
      const next = [...current.entries];
      next.splice(movingRange.start, movingSlice.length);
      next.push(...movingSlice);
      return { ...current, entries: next };
    });
  }, [config.entries, moveSectionBefore]);

  const moveRowToSection = React.useCallback((rowKey: string, sectionKey: string, atStart: boolean) => {
    setConfig((current) => {
      const sectionRange = findSectionRange(current.entries, sectionKey);
      if (!sectionRange) {
        return current;
      }
      const targetIndex = atStart ? sectionRange.start + 1 : sectionRange.endExclusive;
      const next = [...current.entries];
      const fromIndex = next.findIndex((entry) => entry.key === rowKey && entry.type === 'row');
      if (fromIndex < 0) {
        return current;
      }
      const [entry] = next.splice(fromIndex, 1);
      let insertIndex = targetIndex;
      if (fromIndex < insertIndex) {
        insertIndex -= 1;
      }
      next.splice(Math.max(0, Math.min(insertIndex, next.length)), 0, entry);
      return { ...current, entries: next };
    });
  }, []);

  const duplicateRow = React.useCallback((rowKey: string) => {
    setConfig((current) => {
      const index = current.entries.findIndex((entry) => entry.key === rowKey && entry.type === 'row');
      if (index < 0) {
        return current;
      }
      const source = current.entries[index];
      if (source.type !== 'row') {
        return current;
      }
      const sourceKey = source.sourceKey ?? source.key;
      const duplicateKey = `${source.key}__dup_${Date.now()}_${Math.floor(Math.random() * 10_000)}`;
      const duplicateEntry: LayoutEntry = {
        ...source,
        key: duplicateKey,
        sourceKey,
      };
      const nextEntries = [...current.entries];
      nextEntries.splice(index + 1, 0, duplicateEntry);
      return {
        ...current,
        entries: nextEntries,
      };
    });
  }, []);

  const sectionGroups = React.useMemo(() => buildSectionGroups(config.entries), [config.entries]);
  const sectionOptions = React.useMemo(
    () => sectionGroups.map((group) => ({ value: group.section.key, label: group.section.label })),
    [sectionGroups],
  );

  const handleSave = React.useCallback(async () => {
    const next = mergeZReportLayoutConfig(config);
    await onSave(next);
  }, [config, onSave]);

  const handleReset = React.useCallback(async () => {
    setConfig(buildDefaultZReportLayoutConfig());
    await onSave(null);
  }, [onSave]);

  // Export writes the last saved configuration, not the unsaved editor state, so a file always
  // matches what the source tenant actually prints.
  const handleExport = React.useCallback(() => {
    setTransferError(null);
    const exportedAt = new Date();
    const payload = buildLayoutExportFile(initialConfig, { tenantId, exportedAt });
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = buildLayoutExportFilename({ tenantId, exportedAt });
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [initialConfig, tenantId]);

  // An import only loads the file into the editor; it is applied to the tenant when the user
  // presses שמור, so a wrong file is undone by closing the modal.
  const handleImportFile = React.useCallback(async (file: File | null) => {
    // Clear the picker so choosing the same file twice still fires onChange.
    resetFileRef.current?.();
    if (!file) {
      return;
    }
    setTransferError(null);
    setImportedNotice(null);
    let text: string;
    try {
      text = await file.text();
    } catch {
      setTransferError('לא ניתן לקרוא את הקובץ.');
      return;
    }
    const result = parseLayoutImportFile(text);
    if (!result.ok) {
      setTransferError(result.error);
      return;
    }
    setConfig(result.config);
    setCollapsedSections(new Set());
    setTransferError(null);
    setImportedNotice('ההגדרות נטענו מהקובץ. לחץ שמור כדי להחיל אותן.');
  }, []);

  return (
    <Modal
      opened={opened}
      onClose={() => {
        if (!saving) {
          onClose();
        }
      }}
      title={<span className={classes.title}>דוח X/Z מודולרי</span>}
      centered
      radius="md"
      size="90%"
      onContextMenu={(event) => event.preventDefault()}
      classNames={{ body: classes.modalBody }}
      closeButtonProps={{ 'data-testid': 'z-layout-close' } as Record<string, string>}
    >
      {loading ? (
        <div className={classes.loader}><Loader size="sm" /></div>
      ) : (
        <div className={classes.shell} dir="rtl">
          <div className={classes.previewPane}>
            <div className={classes.previewToolbar}>
              <span className={classes.previewLabel}>תצוגה מקדימה</span>
              <SegmentedControl
                value={previewKind}
                onChange={(value) => setPreviewKind(value as 'z' | 'x')}
                data={[
                  { value: 'z', label: 'דוח Z' },
                  { value: 'x', label: 'דוח X' },
                ]}
                disabled={saving}
                size="xs"
                data-testid="z-layout-preview-kind"
              />
            </div>
            <PosReportView report={effectivePreviewReport} variant="screen" layoutConfigOverride={config} />
          </div>

          <div className={classes.editorPane}>
            <h4 className={classes.editorTitle}>הגדרות שדות וכותרות</h4>
            <p className={classes.editorHint}>גרירה של כותרת מזיזה סקשן שלם. לגרירת שורות יש גם בחירת סקשן מהירה ליד כל שורה.</p>
            <div className={classes.entryList}>
              {sectionGroups.map((group, sectionIndex) => {
                const isCollapsed = collapsedSections.has(group.section.key);
                return (
                  <div key={group.section.key} className={classes.sectionCard}>
                    <div
                      className={
                        (dropTarget?.kind === 'section'
                          && dropTarget.sectionKey === group.section.key)
                          ? `${classes.sectionHeader} ${classes.sectionHeaderDropTarget}`
                          : classes.sectionHeader
                      }
                      onDragOver={(event) => {
                        allowDrop(event);
                        const dragging = currentDrag();
                        if (!dragging || dragging.type !== 'section') {
                          return;
                        }
                        setDropTarget({ kind: 'section', sectionKey: group.section.key });
                      }}
                      onDrop={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        const dragging = currentDrag();
                        if (!dragging || dragging.type !== 'section') {
                          return;
                        }
                        moveSectionBefore(dragging.key, group.section.key);
                        endDrag();
                      }}
                    >
                      <div className={classes.entryHead}>
                        <span
                          className={classes.dragHandle}
                          draggable={!saving}
                          onDragStart={(event) => beginDrag(event, group.section.key, 'section')}
                          onDragEnd={endDrag}
                        >
                          <FontAwesomeIcon icon={faGripVertical as IconProp} />
                        </span>
                        <span className={classes.entryType}>סקשן</span>
                        <span className={classes.sectionMeta}>שורות: {group.rows.length}</span>
                        <span className={classes.entryKey}>{group.section.key}</span>
                      </div>
                      <div className={classes.entryControls}>
                        <ActionIcon
                          variant="light"
                          color="gray"
                          onClick={() => toggleSectionCollapsed(group.section.key)}
                          disabled={saving}
                          aria-label={isCollapsed ? 'פתח סקשן' : 'כווץ סקשן'}
                          data-testid={`z-layout-section-collapse-${group.section.key}`}
                        >
                          <FontAwesomeIcon icon={(isCollapsed ? faChevronDown : faChevronUp) as IconProp} />
                        </ActionIcon>
                        <ActionIcon
                          variant="light"
                          color="gray"
                          onClick={() => moveSectionByStep(group.section.key, -1)}
                          disabled={saving || sectionIndex === 0}
                          aria-label="העבר סקשן למעלה"
                          data-testid={`z-layout-section-up-${group.section.key}`}
                        >
                          <FontAwesomeIcon icon={faArrowUp as IconProp} />
                        </ActionIcon>
                        <ActionIcon
                          variant="light"
                          color="gray"
                          onClick={() => moveSectionByStep(group.section.key, 1)}
                          disabled={saving || sectionIndex === sectionGroups.length - 1}
                          aria-label="העבר סקשן למטה"
                          data-testid={`z-layout-section-down-${group.section.key}`}
                        >
                          <FontAwesomeIcon icon={faArrowDown as IconProp} />
                        </ActionIcon>
                        <Checkbox
                          checked={group.section.visible}
                          onChange={(event) => {
                            const checked = event.currentTarget.checked;
                            setEntry(group.section.key, (current) => ({ ...current, visible: checked }));
                          }}
                          label={group.section.visible ? 'מוצג' : 'מוסתר'}
                          disabled={saving}
                          data-testid={`z-layout-section-visible-${group.section.key}`}
                        />
                        <ActionIcon
                          variant="subtle"
                          color={group.section.visible ? 'blue' : 'gray'}
                          onClick={() => setEntry(group.section.key, (current) => ({ ...current, visible: !current.visible }))}
                          disabled={saving}
                          aria-label={group.section.visible ? 'הסתר סקשן' : 'הצג סקשן'}
                          data-testid={`z-layout-section-toggle-visible-${group.section.key}`}
                        >
                          <FontAwesomeIcon icon={(group.section.visible ? faEye : faEyeSlash) as IconProp} />
                        </ActionIcon>
                      </div>
                      <TextInput
                        value={group.section.label}
                        onChange={(event) => {
                          const value = event.currentTarget.value;
                          setEntry(group.section.key, (current) => ({ ...current, label: value }));
                        }}
                        disabled={saving}
                        data-testid={`z-layout-label-${group.section.key}`}
                        {...NO_SELECTION_INPUT_PROPS}
                      />
                    </div>

                    {!isCollapsed && (
                      <>
                        <div
                          className={
                            dropTarget?.kind === 'section-start' && dropTarget.sectionKey === group.section.key
                              ? `${classes.sectionDropZone} ${classes.sectionDropZoneActive}`
                              : classes.sectionDropZone
                          }
                          onDragOver={(event) => {
                            allowDrop(event);
                            const dragging = currentDrag();
                            if (!dragging || dragging.type !== 'row') {
                              return;
                            }
                            setDropTarget({ kind: 'section-start', sectionKey: group.section.key });
                          }}
                          onDrop={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            const dragging = currentDrag();
                            if (!dragging || dragging.type !== 'row') {
                              return;
                            }
                            moveRowToSection(dragging.key, group.section.key, true);
                            endDrag();
                          }}
                        >
                          גרור לכאן כדי להוסיף שורה לתחילת הסקשן
                        </div>

                        <div
                          className={
                            dropTarget?.kind === 'section-body' && dropTarget.sectionKey === group.section.key
                              ? `${classes.sectionRows} ${classes.sectionRowsDropTarget}`
                              : classes.sectionRows
                          }
                          onDragOver={(event) => {
                            allowDrop(event);
                            const dragging = currentDrag();
                            if (!dragging || dragging.type !== 'row') {
                              return;
                            }
                            setDropTarget({ kind: 'section-body', sectionKey: group.section.key });
                          }}
                          onDrop={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            const dragging = currentDrag();
                            if (!dragging || dragging.type !== 'row') {
                              return;
                            }
                            moveRowToSection(dragging.key, group.section.key, false);
                            endDrag();
                          }}
                        >
                          {group.rows.map((row) => {
                            const canonicalRowKey = row.entry.type === 'row' ? (row.entry.sourceKey ?? row.entry.key) : row.entry.key;
                            const isMoneyRow = MONEY_ROW_KEYS.has(canonicalRowKey);
                            const isXOnlyRow = X_ONLY_ROW_KEYS.has(canonicalRowKey);

                            return (
                              <div
                                key={row.entry.key}
                                className={
                                  dropTarget?.kind === 'row' && dropTarget.rowKey === row.entry.key
                                    ? `${classes.rowCard} ${dropTarget.after ? classes.rowDropAfter : classes.rowDropBefore}`
                                    : classes.rowCard
                                }
                                onDragOver={(event) => {
                                  allowDrop(event);
                                  const dragging = currentDrag();
                                  if (!dragging || dragging.type !== 'row') {
                                    return;
                                  }
                                  const rect = event.currentTarget.getBoundingClientRect();
                                  const dropAfter = event.clientY > rect.top + rect.height / 2;
                                  setDropTarget({ kind: 'row', rowKey: row.entry.key, after: dropAfter });
                                }}
                                onDrop={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  const dragging = currentDrag();
                                  if (!dragging || dragging.type !== 'row') {
                                    return;
                                  }
                                  const rect = event.currentTarget.getBoundingClientRect();
                                  const dropAfter = event.clientY > rect.top + rect.height / 2;
                                  moveRowToIndex(dragging.key, dropAfter ? row.index + 1 : row.index);
                                  endDrag();
                                }}
                              >
                                <div className={classes.entryHead}>
                                  <span
                                    className={classes.dragHandle}
                                    draggable={!saving}
                                    onDragStart={(event) => beginDrag(event, row.entry.key, 'row')}
                                    onDragEnd={endDrag}
                                  >
                                    <FontAwesomeIcon icon={faGripVertical as IconProp} />
                                  </span>
                                  <span className={classes.entryType}>שורה</span>
                                  <NativeSelect
                                    className={classes.rowSectionSelect}
                                    value={group.section.key}
                                    onChange={(event) => {
                                      const sectionKey = event.currentTarget.value;
                                      if (!sectionKey || sectionKey === group.section.key) {
                                        return;
                                      }
                                      moveRowToSection(row.entry.key, sectionKey, false);
                                    }}
                                    data={sectionOptions}
                                    disabled={saving}
                                    size="xs"
                                  />
                                  {isXOnlyRow && <span className={classes.scopeTag}>X בלבד</span>}
                                  <span className={classes.entryKey}>{row.entry.key}</span>
                                </div>
                                <div className={classes.entryControls}>
                                  <ActionIcon
                                    variant="light"
                                    color="gray"
                                    onClick={() => duplicateRow(row.entry.key)}
                                    disabled={saving}
                                    aria-label="שכפל שורה"
                                    data-testid={`z-layout-row-duplicate-${row.entry.key}`}
                                  >
                                    <FontAwesomeIcon icon={faCopy as IconProp} />
                                  </ActionIcon>
                                  <ActionIcon
                                    variant={row.entry.negative ? 'filled' : 'light'}
                                    color={row.entry.negative ? 'red' : 'gray'}
                                    onClick={() => setEntry(row.entry.key, (current) => ({ ...current, negative: !current.negative }))}
                                    disabled={saving || !isMoneyRow}
                                    aria-label={row.entry.negative ? 'בטל הצגה כשלילי' : 'הצג כשלילי'}
                                    data-testid={`z-layout-row-negative-${row.entry.key}`}
                                  >
                                    <FontAwesomeIcon icon={faMinus as IconProp} />
                                  </ActionIcon>
                                  <ActionIcon
                                    variant={row.entry.bold ? 'filled' : 'light'}
                                    color={row.entry.bold ? 'dark' : 'gray'}
                                    onClick={() => setEntry(row.entry.key, (current) => ({ ...current, bold: !current.bold }))}
                                    disabled={saving}
                                    aria-label={row.entry.bold ? 'בטל מודגש' : 'סמן מודגש'}
                                    data-testid={`z-layout-row-bold-${row.entry.key}`}
                                  >
                                    <FontAwesomeIcon icon={faBold as IconProp} />
                                  </ActionIcon>
                                  <Checkbox
                                    checked={row.entry.visible}
                                    onChange={(event) => {
                                      const checked = event.currentTarget.checked;
                                      setEntry(row.entry.key, (current) => ({ ...current, visible: checked }));
                                    }}
                                    label={row.entry.visible ? 'מוצג' : 'מוסתר'}
                                    disabled={saving}
                                    data-testid={`z-layout-row-visible-${row.entry.key}`}
                                  />
                                  <ActionIcon
                                    variant="subtle"
                                    color={row.entry.visible ? 'blue' : 'gray'}
                                    onClick={() => setEntry(row.entry.key, (current) => ({ ...current, visible: !current.visible }))}
                                    disabled={saving}
                                    aria-label={row.entry.visible ? 'הסתר שורה' : 'הצג שורה'}
                                    data-testid={`z-layout-row-toggle-visible-${row.entry.key}`}
                                  >
                                    <FontAwesomeIcon icon={(row.entry.visible ? faEye : faEyeSlash) as IconProp} />
                                  </ActionIcon>
                                </div>
                                <TextInput
                                  value={row.entry.label}
                                  onChange={(event) => {
                                    const value = event.currentTarget.value;
                                    setEntry(row.entry.key, (current) => ({ ...current, label: value }));
                                  }}
                                  disabled={saving}
                                  data-testid={`z-layout-label-${row.entry.key}`}
                                  {...NO_SELECTION_INPUT_PROPS}
                                />
                              </div>
                            );
                          })}
                        </div>

                        <div
                          className={
                            dropTarget?.kind === 'section-end' && dropTarget.sectionKey === group.section.key
                              ? `${classes.sectionDropZone} ${classes.sectionDropZoneActive}`
                              : classes.sectionDropZone
                          }
                          onDragOver={(event) => {
                            allowDrop(event);
                            const dragging = currentDrag();
                            if (!dragging || dragging.type !== 'row') {
                              return;
                            }
                            setDropTarget({ kind: 'section-end', sectionKey: group.section.key });
                          }}
                          onDrop={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            const dragging = currentDrag();
                            if (!dragging || dragging.type !== 'row') {
                              return;
                            }
                            moveRowToSection(dragging.key, group.section.key, false);
                            endDrag();
                          }}
                        >
                          גרור לכאן כדי להוסיף שורה לסוף הסקשן
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            {transferError && (
              <div className={classes.transferError} data-testid="z-layout-transfer-error">
                {transferError}
              </div>
            )}
            {!transferError && importedNotice && (
              <div className={classes.transferNotice} data-testid="z-layout-transfer-notice">
                {importedNotice}
              </div>
            )}
            <div className={classes.actions}>
              <Button variant="cancel" onClick={() => { void handleReset(); }} disabled={saving} dataTestId="z-layout-reset">
                ברירת מחדל
              </Button>
              <Button variant="cancel" onClick={handleExport} disabled={saving} dataTestId="z-layout-export">
                ייצוא
              </Button>
              <FileButton
                resetRef={resetFileRef}
                accept="application/json,.json"
                inputProps={{ 'data-testid': 'z-layout-import-input' } as Record<string, string>}
                onChange={(file) => { void handleImportFile(file); }}
              >
                {(fileButtonProps) => (
                  <Button
                    variant="cancel"
                    onClick={fileButtonProps.onClick}
                    disabled={saving}
                    dataTestId="z-layout-import"
                  >
                    ייבוא
                  </Button>
                )}
              </FileButton>
              <Button variant="save" onClick={() => { void handleSave(); }} loading={saving} dataTestId="z-layout-save">
                שמור
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export const ZReportLayoutModal = React.memo(ZReportLayoutModalComponent);
